import { Injectable } from '@angular/core';
import { QueryConfig, QueryEntity } from '@datorama/akita';
import { SurveyStore, SurveyState } from './survey.store';
import { Observable } from 'rxjs';
import { Survey } from './models/survey.model';
import { SurveyType } from './models/survey.enum';
import { map } from 'rxjs/operators';
import { ProcessStatus } from './models/process-status.enum';

const sortByOrdinal = (a: Survey, b: Survey) => {
  if (a.Ordinal < b.Ordinal) {
    return -1;
  }
  if (a.Ordinal > b.Ordinal) {
    return 1;
  }
  return 0;
};

// @dynamic
@Injectable({ providedIn: 'root' })
@QueryConfig({ sortBy: sortByOrdinal })
export class SurveyQuery extends QueryEntity<SurveyState> {
  constructor(protected store: SurveyStore) {
    super(store);
  }

  /** All surveys with supplied type and take amount */
  selectSurveys(surveyType: SurveyType, take = Number.MAX_SAFE_INTEGER): Observable<Survey[]> {
    return this.selectAll({
      filterBy: (entity) => entity.SurveyType === surveyType,
      limitTo: take,
    });
  }

  /** Select all avalaible surveys of a given type, or empty array if none*/
  selectRelevantSurveys(surveyType: SurveyType): Observable<Survey[]> {
    return this.selectAll({
      filterBy: (entity) => entity.SurveyType === surveyType,
    }).pipe(
      map((surveys) => {
        let returnSurveys = !!surveys.filter((survey) => survey.EndDate > survey.RequestTime).length
          ? surveys.filter((survey) => survey.EndDate > survey.RequestTime)
          : ([] as Survey[]);

        /** If survey is predictor then only return processed predictors where user has previously answered */
        return surveyType == SurveyType.Predictor
          ? returnSurveys.filter(
              (predictor) =>
                predictor.ProcessStatus !== ProcessStatus.Processed ||
                (predictor.ProcessStatus == ProcessStatus.Processed && predictor.HasBeenAnsweredByUser)
            )
          : returnSurveys;
      })
    );
  }

  /** Select featured/favorited surveys */
  selectFavoriteSurvey(surveyType: SurveyType) {
    return this.selectAll({ filterBy: (entity) => entity.SurveyType === surveyType }).pipe(
      map((surveys) => {
        //** if any favorites are found, return them, else return empty array */
        let returnSurveys = !!surveys.filter((survey) => survey.Favourite == 1 && survey.EndDate > survey.RequestTime).length
          ? surveys.filter((survey) => survey.Favourite == 1 && survey.EndDate > survey.RequestTime)
          : ([] as Survey[]);

        /** If survey is predictor then only return processed predictors where user has previously answered */
        return surveyType == SurveyType.Predictor
          ? returnSurveys.filter(
              (predictor) =>
                predictor.ProcessStatus !== ProcessStatus.Processed ||
                (predictor.ProcessStatus == ProcessStatus.Processed && predictor.HasBeenAnsweredByUser)
            )
          : returnSurveys;
      })
    );
  }

  /** Select latest featured survey */
  selectHomepageSurvey(surveyType: SurveyType) {
    return this.selectFavoriteSurvey(surveyType).pipe(
      map((surveys) => {
        //** if any favorites are found, return latest, else return empty array */
        let returnSurveys = !!surveys.length
          ? [surveys.sort((a, b) => new Date(b.EndDate).getTime() - new Date(a.EndDate).getTime())[0]]
          : ([] as Survey[]);

        /** If survey is predictor then only return processed predictors where user has previously answered */
        return surveyType == SurveyType.Predictor
          ? returnSurveys.filter(
              (predictor) =>
                predictor.ProcessStatus !== ProcessStatus.Processed ||
                (predictor.ProcessStatus == ProcessStatus.Processed && predictor.HasBeenAnsweredByUser)
            )
          : returnSurveys;
      })
    );
  }

  /** Select score predictors */
  selectScorePredictors() {
    return this.selectRelevantSurveys(SurveyType.ScorePredictor).pipe(
      map((surveys) => {
        return surveys.filter((x) => {
          let endDate = x.SeparateEntryWindow ? x.EntryEndDate : x.EndDate;

          if (!x.HasBeenAnsweredByUser && x.RequestTime > endDate) {
            return false;
          }
          return true;
        });
      }),
      map((surveys) => {
        return surveys.sort((a, b) => {
          if (a.HasBeenAnsweredByUser && b.HasBeenAnsweredByUser) {
            let entryA = a.StartDate;
            if (a.SeparateEntryWindow) {
              entryA = a.EntryStartDate;
            }

            let entryB = b.StartDate;
            if (b.SeparateEntryWindow) {
              entryB = b.EntryStartDate;
            }

            return new Date(entryA).getTime() - new Date(entryB).getTime();
          }
          return a.HasBeenAnsweredByUser ? 1 : -1;
        });
      })
    );
  }
}
